// @ts-strict-ignore
import { WxDrawingElementMesh } from './wx-drawing-element-mesh';
import { Matrix4, Object3D } from 'three';
import { ExportToolUtils } from './export-tool-utils';
import { WxArticleVO, WxCarrierVO, WxConnectionVO, WxProjectArticleVO, WxProjectConnectionVO } from '@article/model/vo/wx-article.vo';
import { ArrayUtils } from '@shared/util/array-utils';
import { ExportToolDataService } from './export-tool-data.service';
import { lastValueFrom } from 'rxjs';

export class WxCarrierElementMesh extends WxDrawingElementMesh {

  private carrier: WxCarrierVO;
  private subShapesByProjectArticleID: WxDrawingElementMesh[] = [];

  public constructor(article: WxArticleVO,
                     private exportToolDataService: ExportToolDataService,
  ) {
    super(article);
  }

  public async invalidateMesh(exportToolUtils: ExportToolUtils, parseConnections: boolean): Promise<void> {
    if (!this.article) {
      return;
    }
    this.subShapesByProjectArticleID = [];
    if (this.article.carrier && ArrayUtils.isNotEmpty(this.article.carrier.projectArticles)) {
      this.carrier = this.article.carrier;
      const childArticleIds: number[] = this.carrier.projectArticles.map((projectArticle: WxProjectArticleVO) => projectArticle.articleId);
      const articles: WxArticleVO[] = await lastValueFrom(this.exportToolDataService.getWxArticlesByIds(childArticleIds));
      return this.nextStep(exportToolUtils, articles, parseConnections);
    } else {
      return super.invalidateMesh(exportToolUtils, parseConnections);
    }
  }

  private async nextStep(exportToolUtils: ExportToolUtils, articles: WxArticleVO[], parseConnections: boolean): Promise<void> {
    if (ArrayUtils.isEmpty(articles)) {
      return;
    }
    try {
      let shape: WxDrawingElementMesh;
      const poleX: number = this.article.shape.poleX > 0 ? 1 : 0;
      const poleY: number = this.article.shape.poleY > 0 ? 1 : 0;
      const poleZ: number = this.article.shape.poleZ > 0 ? 1 : 0;
      for (const projectArticle of this.carrier.projectArticles) {
        const baseId: number = projectArticle.articleId;
        const baseXML: WxArticleVO = articles.find((article: WxArticleVO) => article.itemId === baseId);
        if (!baseXML) {
          continue;
        }

        shape = new WxDrawingElementMesh(baseXML);
        await shape.invalidateMesh(exportToolUtils, parseConnections);
        shape.name = String(projectArticle.articleId);
        this.add(shape);

        if (projectArticle.projectArticleId === 1 || projectArticle.articleId === 99002091) {
          shape.visible = false;
        }

        const projectConnection: WxProjectConnectionVO = this.carrier.projectConnections.find((projectConnection: WxProjectConnectionVO) => {
          return projectConnection.basePin.projectArticleId === projectArticle.projectArticleId;
        });

        this.subShapesByProjectArticleID[projectArticle.projectArticleId] = shape;
        if (projectConnection) {
          const targetProjectArticle: WxProjectArticleVO = this.carrier.projectArticles.find((projectArticle: WxProjectArticleVO) => {
            return projectArticle.projectArticleId === projectConnection.targetPin.projectArticleId;
          });
          const targetId: number = targetProjectArticle.articleId;
          const targetXML: WxArticleVO = articles.find((article: WxArticleVO) => article.itemId === targetId);
          if (!targetXML) {
            throw new Error('Could not find base article in Wx database with id ' + targetId);
          }
          const baseConnection: WxConnectionVO = baseXML.connections.find((connection: WxConnectionVO) => {
            return connection.connectionOrder === projectConnection.basePin.order;
          });
          const targetConnection: WxConnectionVO = targetXML.connections.find((connection: WxConnectionVO) => {
            return connection.connectionOrder === projectConnection.targetPin.order;
          });
          const targetShape: Object3D = this.subShapesByProjectArticleID[projectConnection.targetPin.projectArticleId];

          const usePole: boolean = (!baseConnection || targetId === 99999997);
          const offset: number = usePole ? projectConnection.targetPin.offset * targetXML.lengthMax : 0;

          //bug #1051. Use Pole_Count when creating geometry
          let baseTransform: Matrix4 = ExportToolUtils.translateToConfiguratorTransformation(
            offset * poleX + targetConnection.coordinateX,
            offset * poleY + targetConnection.coordinateY,
            offset * poleZ + targetConnection.coordinateZ,
            targetConnection.rotationX,
            targetConnection.rotationY,
            targetConnection.rotationZ
          );
          if (baseConnection) {
            const subTransform: Matrix4 = ExportToolUtils.translateToConfiguratorTransformation(
              baseConnection.coordinateX,
              baseConnection.coordinateY,
              baseConnection.coordinateZ,
              baseConnection.rotationX,
              baseConnection.rotationY,
              baseConnection.rotationZ
            );
            const inverseSubTransform: Matrix4 = subTransform.invert();
            baseTransform.premultiply(inverseSubTransform);
          }

          const cloneT: Matrix4 = targetShape.matrixWorld.clone();
          cloneT.premultiply(baseTransform);
          baseTransform = cloneT;
          shape.applyMatrix4(baseTransform);
        }
      }
      return;
    } catch (e) {
      throw e;
    }
  }

}
